import {
  AuthConfig,
  Control,
  ControlCodes,
  ControlMonitorEndpointPolicy,
  ControlMonitorInboxPolicy,
  ControlMonitorPolicySetting,
  ControlMonitorUserPolicy,
  NonArchPlatform,
  queries,
  ValidControl,
  ValidControlCode,
} from "@theprelude/core";
import orderBy from "lodash/orderBy";
import { createQuery } from "@/lib/create-query";
import { ResourceType } from "./control-monitor";

type ControlMonitorPolicy =
  | ControlMonitorUserPolicy
  | ControlMonitorInboxPolicy
  | ControlMonitorEndpointPolicy;

const isPlatformPolicy = (
  policy: ControlMonitorPolicy,
): policy is ControlMonitorEndpointPolicy => "platform" in policy;

const isValidControl = (control: Control): control is ValidControl =>
  control.id !== ControlCodes.INVALID && control.id !== ControlCodes.NONE;

export interface CriticalSetting extends ControlMonitorPolicySetting {
  policyId: string;
  policyName: string;
  platform: NonArchPlatform | undefined;
  control: ValidControlCode;
  instanceId: string;
  resourceType: ResourceType;
  resourceCount: number;
}

export const criticalSettingsQuery = (
  authConfig: AuthConfig,
  controls: Control[],
) =>
  createQuery({
    queries: controls.map((c) =>
      queries.scm.policies(authConfig, c.id, c.instance_id),
    ),
    queryFn: (...policyResponses) => {
      const criticalSettings = policyResponses.flatMap((r, i) => {
        const control = controls[i];
        if (!control || !isValidControl(control)) {
          return [];
        }

        const policies = [
          ...(r.endpoint_evaluation?.policies ?? []),
          ...(r.inbox_evaluation?.policies ?? []),
          ...(r.user_evaluation?.policies ?? []),
        ];

        return policies.flatMap((policy) => {
          const settings = policy.settings
            .filter(
              (setting) =>
                setting.critical &&
                !setting.meets_recommendation &&
                !setting.excepted,
            )
            .map(
              (setting): CriticalSetting => ({
                ...setting,
                policyId: policy.id,
                policyName: policy.name,
                platform: isPlatformPolicy(policy)
                  ? policy.platform
                  : undefined,
                control: control.id,
                instanceId: control.instance_id,
                resourceType:
                  "endpoint_count" in policy
                    ? "endpoint"
                    : "inbox_count" in policy
                      ? "inbox"
                      : "user",
                resourceCount:
                  "endpoint_count" in policy
                    ? policy.endpoint_count
                    : "inbox_count" in policy
                      ? policy.inbox_count
                      : policy.user_count,
              }),
            )
            .filter((setting) => setting.resourceCount > 0);

          return settings;
        });
      });

      return orderBy(
        criticalSettings,
        ["meets_recommendation", "resourceCount", "name", "policyName"],
        ["asc", "desc", "asc", "asc"],
      );
    },
  });
